/**
 * Every note about one student, grouped under the name of where it was
 * written (v3.97.0) — the Dashboard's notes peek; the Student Report reuses it.
 *
 *   Teacher notes          students/{id}.teacherNoteEntries (v3.96.0) — "Oct 30, 2026 · Ms. Rivera"
 *   High Frequency Words   the note on an HFW result (v3.95.0)          — "Unit 3 · Oct 30, 2026"
 *   CVC Words              the note on a CVC result (v3.95.0)           — "Kindergarten · Oct 30, 2026"
 *   4K Readiness           the beginning-of-year notes doc (boy_notes)  — "Beginning of the year"
 *
 * Only groups with at least one note; newest first inside a group. Pure: the
 * caller passes what it already read.
 */
import { studentNotesOf, formatNoteDate } from './studentNotes';
import { resultNoteOf } from './resultNote';
import { hfwUnitLabel } from '../constants/hfwWords';
import { CVC_GRADE_LABELS } from '../constants/cvcWords';

const dateOf = (ts) => {
  if (!ts) return null;
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts;
  if (typeof ts.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
};
const dateText = (ts) => {
  const d = dateOf(ts);
  return d ? formatNoteDate(d.toISOString()) : '';
};
const joinMeta = (...parts) => parts.filter(Boolean).join(' · ');
const newestFirst = (a, b) => (b.sortKey || 0) - (a.sortKey || 0);

function resultNoteItems(docs, studentId, labelOf) {
  return (docs || [])
    .filter((d) => d && d.studentId === studentId && resultNoteOf(d))
    .map((d) => {
      const when = dateOf(d.timestamp);
      return {
        id: d.id,
        text: resultNoteOf(d),
        meta: joinMeta(labelOf(d), dateText(d.timestamp)),
        sortKey: when ? when.getTime() : 0,
      };
    })
    .sort(newestFirst);
}

export const hfwNoteLabel = (d) => `${hfwUnitLabel(d.groupLevel)}${d.justThisGroup ? ' (only)' : ''}`;
export const cvcNoteLabel = (d) => CVC_GRADE_LABELS[d.gradeLevel] || '';

/**
 * @param {{ studentId: string, studentDoc?: object, readinessNote?: string,
 *           hfwDocs?: object[], cvcDocs?: object[] }} input
 *   hfwDocs / cvcDocs: plain result objects (`{ id, ...data }`) — any student's;
 *   this picks the student's.
 * @returns {Array<{ key, heading, items: Array<{ id, text, meta }> }>}
 */
export function collectStudentNotes({ studentId, studentDoc, readinessNote, hfwDocs, cvcDocs }) {
  const groups = [];
  const teacher = studentNotesOf(studentDoc).map((e) => ({
    id: e.id,
    text: e.text,
    meta: joinMeta(formatNoteDate(e.createdAt), e.authorName),
  }));
  if (teacher.length) groups.push({ key: 'teacher', heading: 'Teacher notes', items: teacher });
  const hfw = resultNoteItems(hfwDocs, studentId, hfwNoteLabel);
  if (hfw.length) groups.push({ key: 'hfw', heading: 'High Frequency Words', items: hfw.map(({ sortKey, ...i }) => i) });
  const cvc = resultNoteItems(cvcDocs, studentId, cvcNoteLabel);
  if (cvc.length) groups.push({ key: 'cvc', heading: 'CVC Words', items: cvc.map(({ sortKey, ...i }) => i) });
  const readiness = typeof readinessNote === 'string' ? readinessNote.trim() : '';
  if (readiness) groups.push({ key: 'boy', heading: '4K Readiness', items: [{ id: 'boy_notes', text: readiness, meta: 'Beginning of the year' }] });
  return groups;
}

/** How many notes the groups hold. */
export const noteCountOf = (groups) => (groups || []).reduce((n, g) => n + g.items.length, 0);
